import { useState } from "react";
import type { ReactNode } from "react";
import { Collapse } from "./Collapse";

// Collapsible section in the box-flat cream pattern: a chevron + title row that
// toggles a divided body. State is encoded by the chevron rotation alone.
// Open/close animates via the 0fr→1fr grid-rows trick at --duration-base; the
// body therefore stays mounted while collapsed (state survives a collapse) and
// is marked inert so hidden controls can't be tabbed into.
//
// Supports both modes:
//   uncontrolled (defaultOpen) — multiple can be open simultaneously
//   controlled  (open + onToggle) — caller decides; e.g. single-open accordion

interface DisclosureProps {
  title: ReactNode;
  count?: number | string | null;
  headerAction?: ReactNode;
  open?: boolean;
  onToggle?: () => void;
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
  // Wo die Trennlinie zwischen Header und Inhalt sitzt. "top" (Default) ist die uebliche
  // Konvention: die Linie markiert direkt den Beginn des Inhalts, unter dem Header. "bottom"
  // verschiebt sie ans Ende des Inhalts - fuer Faelle, in denen der Header nahtlos in den Inhalt
  // uebergehen soll und die Linie erst den Abschluss zum naechsten Element markiert.
  dividerPlacement?: "top" | "bottom";
}

export function Disclosure({
  title,
  count = null,
  headerAction,
  open: openProp,
  onToggle,
  defaultOpen = false,
  children,
  className = "",
  dividerPlacement = "top",
}: DisclosureProps) {
  const [openState, setOpenState] = useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : openState;

  function toggle() {
    if (isControlled) onToggle?.();
    else setOpenState((v) => !v);
  }

  return (
    <div className={`bg-page box-flat ${className}`}>
      {headerAction ? (
        <div className="hover:bg-ink/[0.06] flex w-full items-center gap-2 transition">
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            className="flex min-w-0 flex-1 items-center gap-2 px-5 py-3 text-left"
          >
            <span
              className={`shrink-0 text-[10px] transition-transform duration-[var(--duration-base)] ${open ? "rotate-90" : ""}`}
            >
              ›
            </span>
            <span className="min-w-0 text-sm font-medium">{title}</span>
            {count != null ? <span className="shrink-0 text-[11px] text-ink/45">({count})</span> : null}
          </button>
          <div className="flex shrink-0 items-center py-2 pr-5">{headerAction}</div>
        </div>
      ) : (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          className="hover:bg-ink/[0.06] flex w-full items-center justify-between gap-2 px-5 py-3 text-left transition"
        >
          <div className="flex items-center gap-2">
            <span
              className={`text-[10px] transition-transform duration-[var(--duration-base)] ${open ? "rotate-90" : ""}`}
            >
              ›
            </span>
            <span className="text-sm font-medium">{title}</span>
            {count != null ? <span className="text-[11px] text-ink/45">({count})</span> : null}
          </div>
        </button>
      )}
      {/* The expand mechanic lives in the Collapse primitive; this component
          only adds the boxed chrome and the chevron trigger around it. */}
      <Collapse open={open}>
        <div className={`${dividerPlacement === "bottom" ? "border-b" : "border-t"} border-ink/5 px-5 py-5`}>
          {children}
        </div>
      </Collapse>
    </div>
  );
}
